import type {
  LaneId,
  PlayerObservationV1,
  VisibleBattlefieldLandV1,
  VisibleCardViewV1,
} from "@fate-kingdom/engine";
import { ContactShadows, Line, RoundedBox } from "@react-three/drei";
import { useThree } from "@react-three/fiber";
import { QuadraticBezierCurve3, Vector3 } from "three";

import type { AlphaCombatPresentationState } from "../alpha/battlefield/CombatPresentation";
import {
  perspectiveBoardScale,
  perspectiveLaneSpread,
  perspectiveLaneX,
} from "../alpha/presentation/perspective-layout";
import { createPhysicalCardFaceSpec, hiddenPhysicalCardFaceSpec } from "./CardFaceTextureCache";
import { CardStack3D } from "./CardStack3D";
import { LaneFormation3D } from "./LaneFormation3D";
import { LaneSlot3D } from "./LaneSlot3D";
import { PhysicalCard3D } from "./PhysicalCard3D";
import {
  FIELD_CARD_SCALE,
  WORLD_FORMATION_Y,
  WORLD_FORMATION_Z,
  WORLD_LAND_Y,
  WORLD_LAND_Z,
  worldTargetPosition,
} from "./world-target-layout";

const LANE_IDS = ["left", "center", "right"] as const;

function LandStack3D({
  laneId,
  land,
  owner,
  position,
  legal,
  selected,
  reducedMotion,
  onActivate,
  hiddenCardInstanceId,
}: {
  readonly laneId: LaneId;
  readonly land: VisibleBattlefieldLandV1;
  readonly owner: "own" | "opponent";
  readonly position: readonly [number, number, number];
  readonly legal: boolean;
  readonly selected: boolean;
  readonly reducedMotion: boolean;
  readonly onActivate: () => void;
  readonly hiddenCardInstanceId?: string | undefined;
}) {
  const visibleCards = land.cards.filter((card) => card.cardInstanceId !== hiddenCardInstanceId);
  return (
    <group>
      {visibleCards.map((card, index) => {
        const top = index === visibleCards.length - 1;
        return (
          <PhysicalCard3D
            face={createPhysicalCardFaceSpec(card, {
              subtitle: `Stage ${String(land.stage)} · Land`,
              stats: [`CONTROL ${String(land.currentControl)}/${String(land.maximumControl)}`],
            })}
            position={[
              position[0] + index * 0.025,
              position[1] + index * 0.032,
              position[2] + (owner === "own" ? -index * 0.015 : index * 0.015),
            ]}
            rotation={[-Math.PI / 2, 0, (owner === "own" ? 0 : Math.PI) + index * 0.012]}
            scale={FIELD_CARD_SCALE.land}
            selected={top && selected}
            legal={top && legal}
            dimmed={false}
            reducedMotion={reducedMotion}
            onActivate={top ? onActivate : undefined}
            sceneMetadata={{
              objectKind: "physical-card",
              cardInstanceId: card.cardInstanceId,
              cardDefinitionId: card.cardDefinitionId,
              cardType: card.type,
              ownerSide: owner,
              laneId,
              zone: "battlefield-land",
              visibleState: "face-up",
            }}
            key={card.cardInstanceId}
          />
        );
      })}
    </group>
  );
}

function AttackPaths3D({
  sourceLaneId,
  targetLaneIds,
  combat,
  laneSpread,
}: {
  readonly sourceLaneId?: LaneId | undefined;
  readonly targetLaneIds: readonly LaneId[];
  readonly combat: AlphaCombatPresentationState | null;
  readonly laneSpread: number;
}) {
  const source = combat?.sourceLaneId ?? sourceLaneId;
  const targets = combat === null ? targetLaneIds : [combat.targetLaneId];
  if (source === undefined) return null;
  return (
    <group>
      {targets.map((targetLaneId) => {
        const from = new Vector3(
          perspectiveLaneX(source, laneSpread),
          0.72,
          WORLD_FORMATION_Z.own - 0.15,
        );
        const to = new Vector3(
          perspectiveLaneX(targetLaneId, laneSpread),
          0.48,
          WORLD_LAND_Z.opponent + 0.18,
        );
        const midpoint = from.clone().lerp(to, 0.5);
        midpoint.y += source === targetLaneId ? 0.55 : 1.15;
        const curve = new QuadraticBezierCurve3(from, midpoint, to);
        return (
          <Line
            points={curve.getPoints(28)}
            color={combat === null ? "#e9ca75" : "#ef806f"}
            lineWidth={combat === null ? 2.2 : 4}
            transparent
            opacity={combat === null ? 0.78 : 0.95}
            key={targetLaneId}
          />
        );
      })}
    </group>
  );
}

function OpponentHand3D({
  count,
  reducedMotion,
}: {
  readonly count: number;
  readonly reducedMotion: boolean;
}) {
  const visible = Math.min(5, count);
  const hidden = hiddenPhysicalCardFaceSpec();
  return (
    <group position={[0, 0, -4.58]}>
      {Array.from({ length: visible }, (_, index) => {
        const center = (visible - 1) / 2;
        const offset = index - center;
        return (
          <PhysicalCard3D
            face={hidden}
            position={[offset * 0.34, 0.28 + Math.abs(offset) * 0.01, 0]}
            rotation={[-Math.PI / 2, 0, Math.PI + offset * -0.055]}
            scale={0.53}
            reducedMotion={reducedMotion}
            sceneMetadata={{
              objectKind: "physical-card",
              ownerSide: "hidden",
              zone: "opponent-hand",
              visibleState: "face-down",
            }}
            key={index}
          />
        );
      })}
    </group>
  );
}

function TableArena3D({
  legalTargetIds,
  targetMode,
  selectedLaneId,
  placementPulseLaneId,
  onChooseLane,
  laneSpread,
}: {
  readonly legalTargetIds: readonly LaneId[];
  readonly targetMode: "own" | "opponent";
  readonly selectedLaneId?: LaneId | undefined;
  readonly placementPulseLaneId?: LaneId | undefined;
  readonly onChooseLane: (laneId: LaneId) => void;
  readonly laneSpread: number;
}) {
  return (
    <group>
      <RoundedBox
        args={[9.35, 0.36, 10.5]}
        radius={0.16}
        smoothness={3}
        position={[0, -0.04, 0]}
        userData={{ sceneOccluderKind: "table-base" }}
      >
        <meshStandardMaterial color="#112a30" metalness={0.09} roughness={0.78} />
      </RoundedBox>
      <RoundedBox
        args={[8.88, 0.12, 10.02]}
        radius={0.1}
        smoothness={2}
        position={[0, 0.19, 0]}
        userData={{ sceneOccluderKind: "table-surface" }}
      >
        <meshStandardMaterial color="#254448" metalness={0.02} roughness={0.94} />
      </RoundedBox>
      <mesh position={[0, 0.29, 0]} userData={{ sceneOccluderKind: "table-divider" }}>
        <boxGeometry args={[8.76, 0.025, 0.075]} />
        <meshStandardMaterial color="#d1b66b" emissive="#7f6427" emissiveIntensity={0.16} />
      </mesh>

      {[-4.55, 4.55].map((x) => (
        <mesh
          position={[x, 0.34, 0]}
          castShadow
          receiveShadow
          userData={{ sceneOccluderKind: "table-rim" }}
          key={`rim-x-${String(x)}`}
        >
          <boxGeometry args={[0.18, 0.31, 10.18]} />
          <meshStandardMaterial color="#5f4b2e" metalness={0.18} roughness={0.62} />
        </mesh>
      ))}
      {[-5.06, 5.06].map((z) => (
        <mesh
          position={[0, 0.34, z]}
          castShadow
          receiveShadow
          userData={{ sceneOccluderKind: "table-rim" }}
          key={`rim-z-${String(z)}`}
        >
          <boxGeometry args={[9.22, 0.31, 0.18]} />
          <meshStandardMaterial color="#6e5630" metalness={0.18} roughness={0.62} />
        </mesh>
      ))}

      {LANE_IDS.map((laneId) => {
        const x = perspectiveLaneX(laneId, laneSpread);
        const legal = legalTargetIds.includes(laneId);
        return (
          <group key={laneId}>
            <RoundedBox
              args={[2.2, 0.035, 8.75]}
              radius={0.08}
              smoothness={2}
              position={[x, 0.27, 0]}
              userData={{ sceneOccluderKind: "lane-surface", laneId }}
            >
              <meshStandardMaterial
                color={legal ? "#234c3c" : "#1b3538"}
                emissive={legal ? "#3f9c64" : "#000000"}
                emissiveIntensity={legal ? 0.13 : 0}
                metalness={0.03}
                roughness={0.94}
              />
            </RoundedBox>
            <LaneSlot3D
              laneId={laneId}
              position={worldTargetPosition(laneId, targetMode, WORLD_LAND_Y, laneSpread)}
              legal={legal}
              selected={selectedLaneId === laneId}
              pulse={placementPulseLaneId === laneId}
              onActivate={onChooseLane}
            />
          </group>
        );
      })}
    </group>
  );
}

export function PerspectiveBattlefield({
  observation,
  legalTargetIds,
  selectedLaneId,
  targetMode,
  placementPulseLaneId,
  attackSourceLaneIds,
  selectedAttackSourceLaneId,
  combat,
  reducedMotion,
  interactionBlocked,
  onChooseLane,
  onSelectAttack,
  onInspectCards,
  transferringCardInstanceId,
}: {
  readonly observation: PlayerObservationV1;
  readonly legalTargetIds: readonly LaneId[];
  readonly selectedLaneId?: LaneId | undefined;
  readonly targetMode: "own" | "opponent";
  readonly placementPulseLaneId?: LaneId | undefined;
  readonly attackSourceLaneIds: readonly LaneId[];
  readonly selectedAttackSourceLaneId?: LaneId | undefined;
  readonly combat: AlphaCombatPresentationState | null;
  readonly reducedMotion: boolean;
  readonly interactionBlocked: boolean;
  readonly onChooseLane: (laneId: LaneId) => void;
  readonly onSelectAttack: (laneId: LaneId) => void;
  readonly onInspectCards: (cards: readonly VisibleCardViewV1[]) => void;
  readonly transferringCardInstanceId?: string | undefined;
}) {
  const viewport = useThree((state) => state.size);
  const boardScale = perspectiveBoardScale(viewport.width, viewport.height);
  const laneSpread = perspectiveLaneSpread(viewport.width, viewport.height);
  const pileX = 4.05 * boardScale[0];
  const policyX = 4.08 * boardScale[0];
  const attackTargets = selectedAttackSourceLaneId === undefined ? [] : legalTargetIds;
  return (
    <group>
      <group scale={boardScale}>
        <TableArena3D
          legalTargetIds={legalTargetIds}
          targetMode={targetMode}
          selectedLaneId={selectedLaneId}
          placementPulseLaneId={placementPulseLaneId}
          onChooseLane={onChooseLane}
          laneSpread={2.55}
        />
      </group>

      {LANE_IDS.map((laneId) => {
        const lane = observation.lanes[laneId];
        const x = perspectiveLaneX(laneId, laneSpread);
        const ownLandLegal = targetMode === "own" && legalTargetIds.includes(laneId);
        const opponentLandLegal = targetMode === "opponent" && legalTargetIds.includes(laneId);
        const ownCards = lane.own.land?.cards ?? [];
        const opponentCards = lane.opponent.land?.cards ?? [];
        return (
          <group key={laneId}>
            {lane.own.land === null ? null : (
              <LandStack3D
                laneId={laneId}
                land={lane.own.land}
                owner="own"
                position={[x, WORLD_LAND_Y, WORLD_LAND_Z.own]}
                legal={ownLandLegal}
                selected={selectedLaneId === laneId}
                reducedMotion={reducedMotion}
                onActivate={() => (ownLandLegal ? onChooseLane(laneId) : onInspectCards(ownCards))}
                hiddenCardInstanceId={transferringCardInstanceId}
              />
            )}
            <LaneFormation3D
              laneId={laneId}
              owner="own"
              side={lane.own}
              position={[x, WORLD_FORMATION_Y, WORLD_FORMATION_Z.own]}
              selected={selectedAttackSourceLaneId === laneId}
              attackReady={attackSourceLaneIds.includes(laneId)}
              reducedMotion={reducedMotion}
              interactionBlocked={interactionBlocked}
              onSelectAttack={onSelectAttack}
              onInspect={onInspectCards}
              hiddenCardInstanceId={transferringCardInstanceId}
            />
            {lane.own.conspiracy === null ? null : (
              <PhysicalCard3D
                face={hiddenPhysicalCardFaceSpec()}
                position={[x + 0.54, 0.35, WORLD_LAND_Z.own + 0.32]}
                rotation={[-Math.PI / 2, 0, 0.08]}
                scale={0.43}
                reducedMotion={reducedMotion}
                onActivate={() => onInspectCards([lane.own.conspiracy!])}
              />
            )}

            {lane.opponent.land === null ? null : (
              <LandStack3D
                laneId={laneId}
                land={lane.opponent.land}
                owner="opponent"
                position={[x, WORLD_LAND_Y, WORLD_LAND_Z.opponent]}
                legal={opponentLandLegal}
                selected={selectedLaneId === laneId}
                reducedMotion={reducedMotion}
                onActivate={() =>
                  opponentLandLegal ? onChooseLane(laneId) : onInspectCards(opponentCards)
                }
                hiddenCardInstanceId={transferringCardInstanceId}
              />
            )}
            <LaneFormation3D
              laneId={laneId}
              owner="opponent"
              side={lane.opponent}
              position={[x, WORLD_FORMATION_Y, WORLD_FORMATION_Z.opponent]}
              selected={false}
              attackReady={false}
              reducedMotion={reducedMotion}
              interactionBlocked={interactionBlocked}
              onInspect={onInspectCards}
              hiddenCardInstanceId={transferringCardInstanceId}
            />
            {!lane.opponent.hasFaceDownCard ? null : (
              <PhysicalCard3D
                face={hiddenPhysicalCardFaceSpec()}
                position={[x - 0.54, 0.35, WORLD_LAND_Z.opponent - 0.32]}
                rotation={[-Math.PI / 2, 0, Math.PI - 0.08]}
                scale={0.43}
                reducedMotion={reducedMotion}
              />
            )}
          </group>
        );
      })}

      <OpponentHand3D count={observation.opponent.handCount} reducedMotion={reducedMotion} />

      {observation.own.deckCount > 0 ? (
        <CardStack3D
          count={observation.own.deckCount}
          position={[pileX, 0.36, 3.15]}
          rotationZ={0.06}
          reducedMotion={reducedMotion}
        />
      ) : null}
      {observation.opponent.deckCount > 0 ? (
        <CardStack3D
          count={observation.opponent.deckCount}
          position={[-pileX, 0.36, -3.55]}
          rotationZ={Math.PI + 0.06}
          reducedMotion={reducedMotion}
        />
      ) : null}
      {observation.own.discard.length > 0 ? (
        <CardStack3D
          count={observation.own.discard.length}
          position={[-pileX, 0.36, 3.15]}
          rotationZ={-0.05}
          topFace={createPhysicalCardFaceSpec(observation.own.discard.at(-1)!)}
          reducedMotion={reducedMotion}
        />
      ) : null}
      {observation.opponent.discard.length > 0 ? (
        <CardStack3D
          count={observation.opponent.discard.length}
          position={[pileX, 0.36, -3.55]}
          rotationZ={Math.PI - 0.05}
          topFace={createPhysicalCardFaceSpec(observation.opponent.discard.at(-1)!)}
          reducedMotion={reducedMotion}
        />
      ) : null}
      {observation.own.exhaust.length > 0 ? (
        <CardStack3D
          count={observation.own.exhaust.length}
          position={[-pileX, 0.36, 0.25]}
          rotationZ={-0.12}
          topFace={createPhysicalCardFaceSpec(observation.own.exhaust.at(-1)!)}
          reducedMotion={reducedMotion}
        />
      ) : null}
      {observation.opponent.exhaust.length > 0 ? (
        <CardStack3D
          count={observation.opponent.exhaust.length}
          position={[pileX, 0.36, -0.25]}
          rotationZ={Math.PI + 0.12}
          topFace={createPhysicalCardFaceSpec(observation.opponent.exhaust.at(-1)!)}
          reducedMotion={reducedMotion}
        />
      ) : null}
      {observation.own.activePolicy === null ? null : (
        <PhysicalCard3D
          face={createPhysicalCardFaceSpec(observation.own.activePolicy, {
            subtitle: "Active Policy",
          })}
          position={[-policyX, 0.37, 1.55]}
          rotation={[-Math.PI / 2, 0, -0.06]}
          scale={0.5}
          reducedMotion={reducedMotion}
          onActivate={() => onInspectCards([observation.own.activePolicy!])}
        />
      )}
      {observation.opponent.activePolicy === null ? null : (
        <PhysicalCard3D
          face={createPhysicalCardFaceSpec(observation.opponent.activePolicy, {
            subtitle: "Active Policy",
          })}
          position={[policyX, 0.37, -1.55]}
          rotation={[-Math.PI / 2, 0, Math.PI + 0.06]}
          scale={0.5}
          reducedMotion={reducedMotion}
          onActivate={() => onInspectCards([observation.opponent.activePolicy!])}
        />
      )}

      <AttackPaths3D
        sourceLaneId={selectedAttackSourceLaneId}
        targetLaneIds={attackTargets}
        combat={combat}
        laneSpread={laneSpread}
      />

      <ContactShadows
        position={[0, 0.285, 0]}
        opacity={0.52}
        scale={11.8}
        blur={2.25}
        far={3.5}
        resolution={512}
        frames={1}
        color="#02070a"
      />
    </group>
  );
}
